import { TextFormatterDisplayWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { NonDatasourceFieldDetailsFragment, NonDatasourceFieldDetails_ColumnFragment, NonDatasourceFieldDetails_DatasourceField_Fragment, NonDatasourceFieldDetails_UpstreamColumnFragment } from '../../../../../gen-src/queries';
import {
  FormattedMarkupWrapperProps,
} from '../../../data-catalog-shared';
import { DescriptionInfo, DescriptionInfoField } from '../fieldColumn/DescriptionInfo';
import { SheetField } from './SheetField';
import {
  FieldCardDetailMarkupWrapper,
  FieldCardDetailTitle,
  FieldCardDetailTitleWrapper,
  NoDescriptionPlaceholder,
} from './VizClientFieldCardStyles';

type Column = NonDatasourceFieldDetails_ColumnFragment;
type UpstreamColumn = NonDatasourceFieldDetails_UpstreamColumnFragment;
type Field = NonDatasourceFieldDetails_DatasourceField_Fragment['remoteField'];

export type VizClientFieldCardDescriptionProps = {
  readonly field: SheetField;
};

export function VizClientFieldCardDescription(props: VizClientFieldCardDescriptionProps): JSX.Element {
  const field = props.field;

  if (field.description) {
    return <DirectDescription description={field.description} />;
  }
  const column = maybeGetColumnFromField(field);
  if (column) {
    if (column.description) {
      return <ColumnDescription column={column} />;
    }
    if (column.referencedByFlowColumnOutputField?.[0]?.parentFields?.[0]?.__typename === 'FlowColumnInputField') {
      const upstreamColumn = column.referencedByFlowColumnOutputField?.[0]?.parentFields?.[0]?.column;
      if (upstreamColumn?.description) {
        return <ColumnDescription column={upstreamColumn} />;
      }
    }
  }
  if (field.__typename === 'DatasourceField') {
    const remoteField = field.remoteField;
    if (remoteField) {
      if (remoteField.description) {
        return <RemoteFieldDescription remoteField={remoteField} />;
      }
      const remoteColumn = maybeGetColumnFromField(remoteField);
      if (remoteColumn && remoteColumn.description) {
        return (
          <RemoteColumnDescription
            remoteColumn={remoteColumn}
            remoteField={remoteField}
          />
        );
      }
      if (remoteField.referencedByFlowFieldOutputField?.[0]?.parentFields?.[0]?.__typename === 'FlowColumnInputField') {
        const remoteUpstreamColumn = remoteField.referencedByFlowFieldOutputField?.[0]?.parentFields?.[0]?.column;
        if (remoteUpstreamColumn?.description) {
          return (
            <RemoteColumnDescription
              remoteColumn={remoteUpstreamColumn}
              remoteField={remoteField}
            />
          );
        }
      }
    }
  }
  return <NoDescription />;
}

function FieldCardDetailMarkup(props: FormattedMarkupWrapperProps): JSX.Element | null {
  const { linksDisabled, ...restProps } = props;
  return (
    <FieldCardDetailMarkupWrapper>
      <TextFormatterDisplayWidget {...restProps} />
    </FieldCardDetailMarkupWrapper>
  );
}

function DirectDescription(props: { description: string }): JSX.Element {
  return (
    <>
      <DescriptionTitle />
      {
        <FieldCardDetailMarkup markup={props.description} />
      }
    </>
  );
}

function ColumnDescription(props: { column: Column | UpstreamColumn }): JSX.Element {
  const description = props.column.description;

  return (
    <>
      <DescriptionTitle sourceColumn={props.column} />
      {
        <FieldCardDetailMarkup markup={description ?? ''} />
      }
    </>
  );
}

function RemoteFieldDescription(props: { remoteField: Field }): JSX.Element {
  return (
    <>
      <DescriptionTitle sourceField={props.remoteField} />
      {
        <FieldCardDetailMarkup markup={props.remoteField?.description ?? ''} />
      }
    </>
  );
}

function RemoteColumnDescription(props: {
  remoteColumn: Column | UpstreamColumn, remoteField: Field
}): JSX.Element {
  const description = props.remoteColumn.description;

  return (
    <>
      <DescriptionTitle sourceColumn={props.remoteColumn} sourceField={props.remoteField} />
      {
        <FieldCardDetailMarkup markup={description ?? ''} />
      }
    </>
  );
}

function NoDescription(): JSX.Element {
  return (
    <>
      <DescriptionTitle />
      <NoDescriptionPlaceholder>{Messages.NoDescriptionAvailable()}</NoDescriptionPlaceholder>
    </>
  );
}

function DescriptionTitle(props: { sourceColumn?: Column | UpstreamColumn, sourceField?: Field }): JSX.Element {
  const sourceField = props.sourceField ? {
    __typename: props.sourceField.__typename,
    datasource: props.sourceField.datasource,
    description: props.sourceField.description,
    id: props.sourceField.id,
    name: props.sourceField.name,
  } as DescriptionInfoField : undefined;

  return (
    <FieldCardDetailTitleWrapper>
      <FieldCardDetailTitle>
        {Messages.Description()}
        <DescriptionInfo direction='horizontal' {...props} sourceField={sourceField} />
      </FieldCardDetailTitle>
    </FieldCardDetailTitleWrapper>
  );
}
function maybeGetColumnFromField(field: NonDatasourceFieldDetailsFragment): Column | null {
  if (field.__typename === 'ColumnField') {
    const columnsConnection = field.columnsConnection;
    if (columnsConnection) {
      const columns = columnsConnection.nodes;
      if (columns && columns.length > 0) {
        const column = columns[0];
        if (column) {
          return column;
        }
      }
    }
  }
  return null;
}

